import * as React from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { useCreateVendor } from '@/hooks/useVendorMutations'
import { ApiError } from '@/lib/api/rawFetch'

// Numeric fields are held as strings (native number inputs emit strings) and
// converted in onSubmit — keeping the form value type uniform avoids the
// input/output type split that zod's `coerce` introduces with react-hook-form.
const numStr = (min: number, label: string) =>
  z.string().refine(
    (v) => v.trim() !== '' && Number.isFinite(Number(v)) && Number(v) >= min,
    { message: `${label} must be ≥ ${min}` }
  )

// Only the name is required. Duty %, freight %, and multiplier are left off the
// create form — the backend defaults them (0 / 0 / 1) and every field is
// editable inline on the table right after the row appears.
const addVendorSchema = z.object({
  vendor: z.string().trim().min(1, 'Vendor name is required'),
  supplier: z.string(),
  lead_time: numStr(1, 'Lead time'),
  buffer_days: numStr(0, 'Buffer'),
  mov_dollars: numStr(0, 'MOV'),
  payment_terms: z.string(),
})

type AddVendorValues = z.infer<typeof addVendorSchema>

const EMPTY: AddVendorValues = {
  vendor: '',
  supplier: '',
  lead_time: '14',
  buffer_days: '3',
  mov_dollars: '250',
  payment_terms: '',
}

export function AddVendorModal({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const create = useCreateVendor()
  const busy = create.isPending

  const form = useForm<AddVendorValues>({
    resolver: zodResolver(addVendorSchema),
    defaultValues: EMPTY,
  })

  React.useEffect(() => {
    if (open) {
      form.reset(EMPTY)
      create.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  async function onSubmit(values: AddVendorValues) {
    const name = values.vendor.trim()
    try {
      await create.mutateAsync({
        vendor: name,
        supplier: values.supplier.trim(),
        lead_time: Math.trunc(Number(values.lead_time)),
        buffer_days: Math.trunc(Number(values.buffer_days)),
        mov_dollars: Number(values.mov_dollars),
        payment_terms: values.payment_terms.trim(),
      })
      toast.success(`Vendor "${name}" added`)
      onOpenChange(false)
    } catch (err) {
      // 409 = the backend already has this exact name. Everything else surfaces
      // its own message.
      const msg =
        err instanceof ApiError && err.status === 409
          ? `Vendor "${name}" already exists`
          : err instanceof Error
            ? err.message
            : 'Failed to add vendor'
      toast.error(msg)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="brand-theme border-[var(--line-warm)] bg-[var(--surface-card)] sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>Add vendor</DialogTitle>
          <DialogDescription>
            Creates a new vendor rule. Every field is editable inline on the
            table afterward.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            noValidate
            className="flex flex-col gap-4"
          >
            <FormField
              control={form.control}
              name="vendor"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Vendor name</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      disabled={busy}
                      autoFocus
                      data-testid="add-vendor-name"
                    />
                  </FormControl>
                  <p className="text-xs text-[var(--mute)]">
                    Must match the Shopify vendor name exactly, or products
                    won&apos;t map to it.
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="supplier"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Supplier{' '}
                    <span className="font-normal text-[var(--mute)]">
                      (optional)
                    </span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Parent supplier, if any"
                      disabled={busy}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-3 gap-3">
              <FormField
                control={form.control}
                name="lead_time"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Lead time (d)</FormLabel>
                    <FormControl>
                      <Input type="number" min={1} {...field} disabled={busy} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="buffer_days"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Buffer (d)</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} {...field} disabled={busy} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="mov_dollars"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>MOV ($)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        step="1"
                        {...field}
                        disabled={busy}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="payment_terms"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Payment terms{' '}
                    <span className="font-normal text-[var(--mute)]">
                      (optional)
                    </span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="e.g. NET 30" disabled={busy} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onOpenChange(false)}
                disabled={busy}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={busy}
                data-testid="add-vendor-submit"
              >
                {busy ? 'Adding…' : 'Add vendor'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
